import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule, EVENT_MANAGER_PLUGINS} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';

import { FormsModule } from '@angular/forms';

import {registerLocaleData} from '@angular/common';
import {HttpClientModule} from '@angular/common/http';
import localeEs from '@angular/common/locales/es';

import {LoadingBarHttpClientModule} from '@ngx-loading-bar/http-client';
import {LoadingBarRouterModule} from '@ngx-loading-bar/router';
import {LoadingBarModule} from '@ngx-loading-bar/core';

import * as localForage from 'localforage';
import * as Sentry from '@sentry/angular';
import {TranslocoService} from '@ngneat/transloco';

import { forceUpdate, persistStateInitFactory, preloadTranslations } from '@patientlynk3/app-initializers';
import {environment} from '@patientlynk3/environment';
import {VueEventModifiersPlugin} from '@patientlynk3/utils/event-manager.plugin';
import {IntroSectionComponentModule} from '@patientlynk3/components/intro-section/intro-section.component';

import {AppRoutingModule} from '@patientlynk3/app-routing.module';
import {AppComponent} from '@patientlynk3/app.component';
import {NotifyBannerComponent} from '@patientlynk3/components/notify-banner/notify-banner.component';

import {AuthModule} from '@patientlynk3/services/auth.module';
import {NotFoundComponent} from '@patientlynk3/views/not-found/not-found.component';
import {LoginComponent} from '@patientlynk3/views/login/login.component';
import {TranslocoRootModule} from '@patientlynk3/transloco-root.module';
import { NavbarModule } from '@patientlynk3/navbar/navbar.module';
import { LoginFormComponentModule } from '@patientlynk3/components/login-form/login-form.component';
import { ProviderComunicationComponent } from "@patientlynk3/views/provider-comunication/provider-comunication.component";
import { ConversationCardComponentModule } from "@patientlynk3/components/provider-comunication/conversation-card/conversation-card.component";
import { MessageCardComponentModule } from "@patientlynk3/components/provider-comunication/message-card/message-card.component"
import { SvgIconComponentModule } from '@patientlynk3/components/svg-icon/svg-icon.component';
import { QuicklinkModule } from 'ngx-quicklink';
import { NgxHotjarModule, NgxHotjarRouterModule } from 'ngx-hotjar';
import { NgSelectModule } from '@ng-select/ng-select';
import { IonicModule , IonicRouteStrategy} from '@ionic/angular';
import { RECAPTCHA_SETTINGS, RecaptchaSettings, RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';

// import * as packageInfo from '@root/package.json';
import { RewriteFrames } from '@sentry/integrations';
import { BrowserTracing } from '@sentry/tracing/dist/browser';
import { TraceService } from '@sentry/angular';
import { Router, RouteReuseStrategy } from '@angular/router';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';

import { AbartysErrorHandlerFactory } from '@patientlynk3/utils/errorHandlers';
import {
  PatientlynkIntroModalComponentModule
} from '@app/components/patientlynk-intro-modal/patientlynk-intro-modal.component';
import { ConfirmPhoneComponentModule } from './components/confirm-phone/confirm-phone.component';
import {AppsBannerComponentModule} from '@app/components/apps-banner/apps-banner.component';
import { InvalidSharedLabComponent } from './views/invalid-shared-lab/invalid-shared-lab.component';

registerLocaleData(localeEs);
/*
Sentry.init({
  dsn: 'https://3344e9fe51f94651a0e06e876199327c@sentry.io/1918694',
  environment: environment.name,
  release: 'patientlynk@' + 3,
  ignoreErrors: [
    'ExpressionChangedAfterItHasBeenCheckedError',
    'a[b].target.className.indexOf',
    'ResizeObserver loop',
    'access property "setAttribute"'
  ],
  enabled: window.location.hostname !== 'localhost',
  integrations: [
    new RewriteFrames(),
    new BrowserTracing({
      tracingOrigins: ['localhost', /https:\/\/(.*)patientlynk.abartyshealth.com/, 'https://portal.labinfopr.com'],
      routingInstrumentation: Sentry.routingInstrumentation
    })
  ],
  tracesSampleRate: 0.5,
});
*/

// replace for now to disable sentry error logging
Sentry.init({
  enabled: false,
});

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    NotifyBannerComponent,
    NotFoundComponent,
    ProviderComunicationComponent,
    InvalidSharedLabComponent,
  ],
  imports: [
    FormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    QuicklinkModule,
    TranslocoRootModule,
    LoadingBarHttpClientModule,
    LoadingBarRouterModule,
    LoadingBarModule,
    NavbarModule,
    AuthModule,
    ConversationCardComponentModule,
    MessageCardComponentModule,
    RecaptchaV3Module,
    ConfirmPhoneComponentModule,
    LoginFormComponentModule,
    IntroSectionComponentModule, // temp
    SvgIconComponentModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false,
    }),
    BsDatepickerModule.forRoot(),
    NgxHotjarModule.forRoot('2193438'),
    NgxHotjarRouterModule,
    NgSelectModule,
    IonicModule.forRoot(),
    PatientlynkIntroModalComponentModule,
    AppsBannerComponentModule
  ],
  providers: [
    FileOpener,
    {
      provide: ErrorHandler,
      useFactory: AbartysErrorHandlerFactory,
      deps: [SwUpdate],
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: EVENT_MANAGER_PLUGINS,
      useClass: VueEventModifiersPlugin,
      multi: true
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.googleReCaptcha
    },
    { provide: 'persistStorage', useValue: localForage },
    { provide: APP_INITIALIZER, useFactory: forceUpdate, multi: true, deps: [SwUpdate] },
    { provide: APP_INITIALIZER, useFactory: persistStateInitFactory, multi: true, deps: [TranslocoService, TraceService] },
    { provide: APP_INITIALIZER, useFactory: preloadTranslations, multi: true, deps: [TranslocoService] },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy }
  ],
  bootstrap: [AppComponent],
})

export class AppModule { }
